import { useMutation, useQuery, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getNavigationMenuQueryKey } from "./useNavigationMenu";
import { getFooterMenuQueryKey } from "./useFooterMenu";

export type MenuItemDto = {
    id: number;
    label: string;
    i18nKey?: string;
    weight: number;
    url?: string;
    icon?: string;
    moduleFeatureKey?: string;
    isActive: boolean;
    parent?: MenuItemDto;
    children?: MenuItemDto[];
    permission?: {
        "@id": string;
        id: number;
        identifier: string;
    };
};

export type MenuDto = {
    id: number;
    name: string;
    type: string;
    canBeDeleted: boolean;
    items?: MenuItemDto[];
};

export type CreateMenuItemData = {
    label: string;
    menuId: number;
    parentId?: number | null;
    weight: number;
    url?: string;
    icon?: string;
    moduleFeatureKey?: string;
    isActive?: boolean;
    permissionId?: number;
};

export type UpdateMenuItemData = {
    label?: string;
    parentId?: number | null;
    weight?: number;
    url?: string;
    icon?: string;
    moduleFeatureKey?: string;
    isActive?: boolean;
    permissionId?: number;
};

export type BulkReorderData = {
    items: Array<{
        id: number;
        weight: number;
        parentId?: number | null;
    }>;
    menuId?: number;
};

const menuAdminQueryKey = "menuAdmin" as const;

export const useMenuAdmin = () => {
    const queryClient = useQueryClient();

    // Get all menus for admin
    const getMenus = useQuery({
        queryKey: [menuAdminQueryKey, "menus"],
        queryFn: async (): Promise<MenuDto[]> => {
            const response = await loggedInApiCall.get("/api/menus");
            return response.data["hydra:member"] || response.data;
        },
    });

    // Create menu item
    const createMenuItem = useMutation({
        mutationFn: async (data: CreateMenuItemData): Promise<MenuItemDto> => {
            // Transform data to match API expectations
            const { menuId, parentId, permissionId, ...rest } = data;
            const apiData = {
                ...rest,
                menu: `/api/menus/${menuId}`,
                ...(parentId && { parent: `/api/menu_items/${parentId}` }),
                ...(permissionId !== undefined && {
                    permission: permissionId ? `/api/permissions/${permissionId}` : null,
                }),
            };
            const response = await loggedInApiCall.post("/api/menu_items", apiData);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([menuAdminQueryKey]);
            queryClient.invalidateQueries([getNavigationMenuQueryKey]);
            queryClient.invalidateQueries([getFooterMenuQueryKey]);
        },
    });

    // Update menu item
    const updateMenuItem = useMutation({
        mutationFn: async ({ id, ...data }: UpdateMenuItemData & { id: number }): Promise<MenuItemDto> => {
            // Transform data to match API expectations
            const { parentId, permissionId, ...rest } = data;
            const apiData = {
                ...rest,
                ...(parentId !== undefined && { parent: parentId ? `/api/menu_items/${parentId}` : null }),
                ...(permissionId !== undefined && {
                    permission: permissionId ? `/api/permissions/${permissionId}` : null,
                }),
            };
            const response = await loggedInApiCall.put(`/api/menu_items/${id}`, apiData);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([menuAdminQueryKey]);
            queryClient.invalidateQueries([getNavigationMenuQueryKey]);
            queryClient.invalidateQueries([getFooterMenuQueryKey]);
        },
    });

    // Delete menu item
    const deleteMenuItem = useMutation({
        mutationFn: async (id: number): Promise<void> => {
            await loggedInApiCall.delete(`/api/menu_items/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([menuAdminQueryKey]);
            queryClient.invalidateQueries([getNavigationMenuQueryKey]);
            queryClient.invalidateQueries([getFooterMenuQueryKey]);
        },
    });

    // Bulk reorder
    const bulkReorder = useMutation({
        mutationFn: async ({
            menuId = 1,
            ...data
        }: BulkReorderData): Promise<{ success: boolean; message?: string }> => {
            const response = await loggedInApiCall.post(`/api/menus/${menuId}/reorder-items`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([menuAdminQueryKey]);
            queryClient.invalidateQueries([getNavigationMenuQueryKey]);
            queryClient.invalidateQueries([getFooterMenuQueryKey]);
        },
        onError: (error) => {
            // Reorder mutation failed - error handled by React Query
        },
    });

    return {
        // Queries
        menus: getMenus.data || [],
        isLoading: getMenus.isLoading,
        error: getMenus.error,

        // Mutations
        createMenuItem,
        updateMenuItem,
        deleteMenuItem,
        bulkReorder,

        // States
        isCreating: createMenuItem.isLoading,
        isUpdating: updateMenuItem.isLoading,
        isDeleting: deleteMenuItem.isLoading,
        isReordering: bulkReorder.isLoading,
    };
};
